import React, { useCallback, useEffect, useMemo, useState } from "react";

import TaskDrawer from "./models/tasks_model/TaskDrawer";
import PageHeader from "../../../components/reusable/management/PageHeader";
import TaskStatsCards from "./models/tasks_model/TaskStatsCards";
import TaskToolbar from "./models/tasks_model/TaskToolbar";
import { taskColumns } from "./models/tasks_model/TaskTableData";
import DynamicTable from "../../../components/reusable/DynamicTable";
import { caApi } from "../../../backend_urls/caApi";
import TaskCreate from "./models/tasks_model/create_tasks/TaskCreate";

const EMPTY_FILTERS = {
    search: "",
    status: "",
    priority: "",
};

// ============================================================
// EMPLOYEE
// ============================================================
const getTaskEmployee = (task) => {
    const assignments = Array.isArray(task?.assignments) ? task.assignments : [];

    const assignment =
        assignments.find((item) => item?.role === "primary") || assignments[0];

    if (!assignment) {
        return {
            id: null,
            name: "Unassigned",
            role: "",
            profile_image: null,
        };
    }

    const employee = assignment.employee || {};

    const user = employee.user || {};

    return {
        id: employee.id || assignment.employee_id || null,

        name:
        employee.name ||
        employee.employee_name ||
        assignment.employee_name ||
        user.user_full_name ||
        user.name ||
        user.email ||
        "Unknown",

        role:
        employee.role ||
        employee.designation ||
        assignment.employee_designation ||
        "",

        profile_image:
        employee.profile_image ||
        employee.user_profile_image ||
        user.profile_image ||
        user.user_profile_image ||
        null,
    };
};

// ============================================================
// STATUS
// ============================================================
const formatStatus = (status) => {
    const labels = {
        draft: "Draft",
        pending: "Pending",
        in_progress: "In Progress",
        paused: "Paused",
        review: "Review",
        completed: "Completed",
        cancelled: "Cancelled",
    };

    return labels[status] || status || "Unknown";
};

// ============================================================
// PRIORITY
// ============================================================
const formatPriority = (priority) => {
    const labels = {
        low: "Low",
        medium: "Medium",
        high: "High",
        critical: "Critical",
    };

    return labels[priority] || priority || "Unknown";
};

// ============================================================
// DATE
// ============================================================
const formatDate = (value) => {
    if (!value) { return "—"; }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) { return value; }

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

// ============================================================
// PROGRESS
// ============================================================

const getProgress = (status) => {
    switch (status) {
        case "draft":
        return 0;

        case "pending":
        return 0;

        case "in_progress":
        return 50;

        case "paused":
        return 50;

        case "review":
        return 85;

        case "completed":
        return 100;

        case "cancelled":
        return 0;

        default:
        return 0;
    }
};

// ============================================================
// NORMALIZE
// ============================================================
const normalizeTask = (task) => {
    const employee = getTaskEmployee(task);

    return {
        ...task,

        business:
            task.business_name ||
            task.business?.business_name ||
            task.business?.name ||
            "—",

        employee,
        priority: formatPriority(task.priority),
        raw_priority: task.priority,
        status: formatStatus(task.status),
        raw_status: task.status,
        due_date_raw: task.due_date,
        due_date: formatDate(task.due_date),
        progress: getProgress(task.status),
    };
};

// ============================================================
// PAGE
// ============================================================

const CATasksPage = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [selectedTask, setSelectedTask] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [workflowLoading, setWorkflowLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [createTaskOpen, setCreateTaskOpen] = useState(false);

    // ========================================================
    // LOAD TASKS
    // ========================================================
    const loadTasks = useCallback(async (showInitialLoader = false) => {
        try {
        if (showInitialLoader) { setLoading(true);
        } else { setRefreshing(true); }

        setError("");

        const response = await caApi.getTasks();
        const responseData = response?.data ?? response;

        const taskList = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.results)
            ? responseData.results
            : [];

        setTasks(taskList.map(normalizeTask));
        } catch (err) {
            console.error("Failed to load tasks:", err);

            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Unable to load tasks.",
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { loadTasks(true); }, [loadTasks]);

    // ========================================================
    // CREATE
    // ========================================================
    const handleCreateTask = useCallback(() => { setCreateTaskOpen(true); }, []);
    const handleCloseCreateTask = useCallback(() => { setCreateTaskOpen(false); }, []);

    const handleTaskCreated = useCallback(async () => {
        setCreateTaskOpen(false);

        await loadTasks(false);
    }, [loadTasks]);

    // ========================================================
    // FILTERS
    // ========================================================
    const filteredTasks = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return tasks.filter((task) => {
            const matchesSearch =
                !search ||
                task.title?.toLowerCase().includes(search) ||
                task.description?.toLowerCase().includes(search) ||
                task.business?.toLowerCase().includes(search) ||
                task.employee?.name?.toLowerCase().includes(search);

            const matchesStatus =
                !filters.status || task.raw_status === filters.status;

            const matchesPriority =
                !filters.priority || task.raw_priority === filters.priority;

            return matchesSearch && matchesStatus && matchesPriority;
        });
    }, [tasks, filters]);

    // ========================================================
    // STATS
    // ========================================================

    const stats = useMemo(() => {
        const now = new Date();

        let open = 0;
        let inProgress = 0;
        let completed = 0;
        let overdue = 0;
        let paused = 0;

        tasks.forEach((task) => {
            switch (task.raw_status) {
                case "draft":
                case "pending":
                open += 1;
                break;

                case "in_progress":
                inProgress += 1;
                break;

                case "completed":
                completed += 1;
                break;

                case "paused":
                paused += 1;
                break;

                default:
                break;
            }

            if (
                !["draft", "completed", "cancelled"].includes(task.raw_status) &&
                task.due_date &&
                task.due_date !== "—"
            ) {
                const dueDate = new Date(task.due_date);

                if (!Number.isNaN(dueDate.getTime()) && dueDate < now) {
                    overdue += 1;
                }
            }
        });

        return {
            open,
            inProgress,
            completed,
            overdue,
            paused,
        };
    }, [tasks]);

    // ========================================================
    // FILTER HANDLERS
    // ========================================================

    const handleSearchChange = useCallback((value) => {
        setFilters((current) => ({ ...current, search: value }));
    }, []);

    const handleStatusChange = useCallback((value) => {
        setFilters((current) => ({ ...current, status: value }));
    }, []);

    const handlePriorityChange = useCallback((value) => {
        setFilters((current) => ({ ...current, priority: value }));
    }, []);

    const handleResetFilters = useCallback(() => { setFilters(EMPTY_FILTERS); }, []);

    // ========================================================
    // OPEN TASK
    // ========================================================
    const handleViewTask = useCallback(async (task) => {
        setSelectedTask(task);
        setDrawerOpen(true);

        try {
            const response = await caApi.getTask(task.id);
            const responseData = response?.data ?? response;
            if (responseData) { setSelectedTask(normalizeTask(responseData)); }
        } catch (err) { console.error("Failed to load task details:", err); }
    }, []);

    // ========================================================
    // CLOSE DRAWER
    // ========================================================
    const handleCloseDrawer = useCallback(() => {
        if (workflowLoading || editLoading) { return; }

        setDrawerOpen(false);
        setSelectedTask(null);
    }, [workflowLoading, editLoading]);

    // ========================================================
    // REFRESH SELECTED TASK
    // ========================================================

    const refreshSelectedTask = useCallback(async (taskId) => {
        const response = await caApi.getTask(taskId);

        const responseData = response?.data ?? response;
        if (!responseData) { return null; }

        const normalized = normalizeTask(responseData);
        setSelectedTask(normalized);
        return normalized;
    }, []);

    // ========================================================
    // WORKFLOW
    // ========================================================

    const handleWorkflow = useCallback(
        async (action, task, employeeId) => {
            if (!task?.id) { return; }

            try {
                setWorkflowLoading(true);
                setError("");

                let response;

                switch (action) {
                    case "start":
                        response = await caApi.startTask(task.id, employeeId);
                        break;

                    case "pause":
                        response = await caApi.pauseTask(task.id, employeeId);
                        break;

                    case "resume":
                        response = await caApi.resumeTask(task.id, employeeId);
                        break;

                    case "complete":
                        response = await caApi.completeTask(task.id, employeeId);
                        break;

                    case "cancel":
                        response = await caApi.cancelTask(task.id);
                        break;

                    case "draft":
                        response = await caApi.draftTask(task.id);
                        break;

                    default:
                        throw new Error(`Unsupported action: ${action}`);
                }

                const responseData = response?.data ?? response;

                // ------------------------------------------------
                // Update drawer immediately from workflow response
                // ------------------------------------------------
                if (responseData) { setSelectedTask(normalizeTask(responseData)); }

                // ------------------------------------------------
                // Refresh table
                // ------------------------------------------------
                await loadTasks(false);

                // ------------------------------------------------
                // Fetch latest drawer data so assignments etc.
                // are definitely current.
                // ------------------------------------------------
                await refreshSelectedTask(task.id);
            } catch (err) {
                console.error(`Failed to ${action} task:`, err);

                setError(
                    err?.response?.data?.detail ||
                        err?.response?.data?.message ||
                        `Unable to ${action} task.`,
                );
            } finally { setWorkflowLoading(false); }
        },
        [loadTasks, refreshSelectedTask],
    );

    // ========================================================
    // UPDATE TASK
    // ========================================================
    const handleUpdateTask = useCallback(
        async (taskId, data) => {
            try {
                setEditLoading(true);

                setError("");

                const response = await caApi.updateTask(taskId, data);

                const responseData = response?.data ?? response;
                if (responseData) { setSelectedTask(normalizeTask(responseData)); }

                await loadTasks(false);

                await refreshSelectedTask(taskId);

                return responseData;
            } catch (err) {
                console.error("Failed to update task:", err);

                const message =
                    err?.response?.data?.detail ||
                    err?.response?.data?.message ||
                    "Unable to update task.";

                setError(message);

                throw err;
            } finally { setEditLoading(false); }
        },
        [loadTasks, refreshSelectedTask],
    );

    // ========================================================
    // TABLE ACTIONS
    // ========================================================

    const actions = useMemo(
        () => [
            {
                label: "View",
                title: "View Task",
                onClick: handleViewTask,
            },
        ],
        [handleViewTask],
    );

    // ========================================================
    // RENDER
    // ========================================================
    return (
        <div className="ca-tasks-page">
            <PageHeader
                title="Tasks"
                subtitle="Create, assign and manage tasks for your firm's employees."
                actions={[
                    {
                        label: "+ Add Task",
                        onClick: handleCreateTask,
                    },
                    {
                        label: "Refresh",
                        onClick: () => loadTasks(false),
                    },
                ]}
            />

            {error && (
                <div
                    style={{
                        marginBottom: 16,
                        padding: "12px 16px",
                        borderRadius: 8,
                        background: "#fef2f2",
                        color: "#b91c1c",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <span>{error}</span>

                    <button type="button" onClick={() => setError("")}>
                        ×
                    </button>
                </div>
            )}

            <TaskStatsCards stats={stats} />

            <TaskToolbar
                filters={filters}
                onSearchChange={handleSearchChange}
                onStatusChange={handleStatusChange}
                onPriorityChange={handlePriorityChange}
                onReset={handleResetFilters}
                taskCount={filteredTasks.length}
                totalCount={tasks.length}
                loading={loading || refreshing}
            />

            <DynamicTable
                columns={taskColumns}
                data={filteredTasks}
                onAction={actions}
                loading={loading}
            />

            {selectedTask && (
                <TaskDrawer
                    task={selectedTask}
                    isOpen={drawerOpen}
                    onClose={handleCloseDrawer}
                    onWorkflow={handleWorkflow}
                    onUpdateTask={handleUpdateTask}
                    workflowLoading={workflowLoading}
                    editLoading={editLoading}
                />
            )}

            <TaskCreate
                isOpen={createTaskOpen}
                onClose={handleCloseCreateTask}
                onSuccess={handleTaskCreated}
            />
        </div>
    );
};

export default CATasksPage;
